/* luxury-drawer.js
 * One controller for every overlay surface: mobile menu, and later the
 * filter sheet. Shared because focus management is the part themes usually
 * get wrong, and it should only be written once.
 *
 * Contract:
 *   <button data-drawer-open="menu">      opens
 *   <div id="menu" data-drawer>           the surface
 *     <button data-drawer-close>          closes
 *   <div data-drawer-scrim>               shared scrim, one per page
 */
(function () {
  'use strict';

  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  var openDrawer = null;
  var lastTrigger = null;
  var savedScrollY = 0;

  function scrim() { return document.querySelector('[data-drawer-scrim]'); }

  function focusables(el) {
    return Array.prototype.filter.call(el.querySelectorAll(FOCUSABLE), function (node) {
      return node.offsetParent !== null || node === document.activeElement;
    });
  }

  function lockPage() {
    savedScrollY = window.scrollY;
    document.body.style.top = '-' + savedScrollY + 'px';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.classList.add('luxury-scroll-locked');
  }

  function unlockPage() {
    document.body.classList.remove('luxury-scroll-locked');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, savedScrollY);
  }

  function open(el, trigger) {
    if (openDrawer) close();
    openDrawer = el;
    lastTrigger = trigger || document.activeElement;

    el.setAttribute('data-open', 'true');
    el.setAttribute('aria-hidden', 'false');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');

    var s = scrim();
    if (s) s.setAttribute('data-open', 'true');

    lockPage();

    // Wait a frame before moving focus, otherwise Safari scrolls the still
    // hidden element into view.
    window.requestAnimationFrame(function () {
      var first = el.querySelector('[data-drawer-autofocus]') || focusables(el)[0];
      if (first) first.focus();
    });

    document.dispatchEvent(new CustomEvent('luxury:drawer:open', { detail: { drawer: el } }));
  }

  function close() {
    if (!openDrawer) return;
    var el = openDrawer;

    el.setAttribute('data-open', 'false');
    el.setAttribute('aria-hidden', 'true');

    var s = scrim();
    if (s) s.setAttribute('data-open', 'false');

    var openers = document.querySelectorAll('[data-drawer-open][aria-expanded="true"]');
    Array.prototype.forEach.call(openers, function (btn) { btn.setAttribute('aria-expanded', 'false'); });

    unlockPage();

    if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();

    openDrawer = null;
    lastTrigger = null;

    document.dispatchEvent(new CustomEvent('luxury:drawer:close', { detail: { drawer: el } }));
  }

  document.addEventListener('click', function (event) {
    var opener = event.target.closest && event.target.closest('[data-drawer-open]');
    if (opener) {
      var target = document.getElementById(opener.getAttribute('data-drawer-open'));
      if (target) {
        event.preventDefault();
        if (target.getAttribute('data-open') === 'true') close();
        else open(target, opener);
      }
      return;
    }
    if (event.target.closest && (event.target.closest('[data-drawer-close]') || event.target.closest('[data-drawer-scrim]'))) {
      event.preventDefault();
      close();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (!openDrawer) return;

    if (event.key === 'Escape') { event.preventDefault(); close(); return; }
    if (event.key !== 'Tab') return;

    var items = focusables(openDrawer);
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];

    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });

  // Accordion rows inside the drawer.
  document.addEventListener('click', function (event) {
    var row = event.target.closest && event.target.closest('[data-accordion-toggle]');
    if (!row) return;
    var panel = document.getElementById(row.getAttribute('aria-controls'));
    if (!panel) return;
    var isOpen = row.getAttribute('aria-expanded') === 'true';
    row.setAttribute('aria-expanded', String(!isOpen));
    panel.setAttribute('data-open', String(!isOpen));
  });

  // Close on resize past the mobile breakpoint so the page is never left
  // locked behind an invisible drawer.
  var mq = window.matchMedia('(min-width: 990px)');
  var onChange = function (e) { if (e.matches && openDrawer) close(); };
  if (mq.addEventListener) mq.addEventListener('change', onChange);
  else if (mq.addListener) mq.addListener(onChange);

  window.LuxuryDrawer = { open: open, close: close };
})();
